import flatten from "lodash/flatten";
import React from "react";
import { Utils } from "../../common/utils";
import { isReadonlyChunk } from "../common";
import { type FormItemSelection } from "../model";
import TemplateEditSection from "./TemplateEditSection";

interface TemplateEditFormProps {
  allFormSections: server.dto.AdvancingFormSection[]
  templateItems: server.dto.AdvancingTemplateItem[]
  onChange: (templateItems: server.dto.AdvancingTemplateItem[]) => void
}

export const getDefaultFormItems = (allFormSections: server.dto.AdvancingFormSection[]): server.dto.AdvancingTemplateItem[] => {
  const formItems = flatten(allFormSections.map(section => section.formItems));

  return formItems.map(item => ({
    chunkId: item.chunkId,
    readOnly: isReadonlyChunk(item.chunkId, item.type.value),
    required: false
  }));
};

export const TemplateEditForm = (props: TemplateEditFormProps) => {
  const onTemplateItemChange = (item: server.dto.AdvancingTemplateItem, newValue: FormItemSelection) => {
    const idx = props.templateItems.findIndex(tItem => tItem.chunkId === item.chunkId);
    const exists = idx !== -1;
    const templateItemListCopy = [...props.templateItems];
    if (newValue === "hidden") {
      const newTemplatesList = props.templateItems.filter(tItem => tItem.chunkId !== item.chunkId);
      props.onChange(newTemplatesList);
      return;
    } else if (!exists) { // It was "hidden" before
      templateItemListCopy.push(item);
    } else { // It just had some props changed
      templateItemListCopy[idx] = item;
    }

    props.onChange(templateItemListCopy);
  }

  const onSectionSettingChange = (section: server.dto.AdvancingFormSection, newValue: FormItemSelection) => {
    let formItems: server.dto.AdvancingFormItem[] = section.formItems;
    if (newValue === "optional" || newValue === "required") {
      formItems = formItems.filter(fItem => !isReadonlyChunk(fItem.chunkId, fItem.type.value));
    }
    const templateList: server.dto.AdvancingTemplateItem[] = formItems.map(fItem => ({
      chunkId: fItem.chunkId,
      readOnly: newValue === "readonly",
      required: newValue === "required"
    }));

    if (newValue === "hidden") {
      // Remove all those template list items
      const listWithoutItemsFromSection = props.templateItems.filter(tItem => templateList.find(tListItem => tListItem.chunkId === tItem.chunkId) == null);
      props.onChange(listWithoutItemsFromSection);
      return;
    }

    const upsertedList = Utils.addAndReplaceExisting(props.templateItems, templateList, (a, b) => a.chunkId === b.chunkId);
    props.onChange(upsertedList);
  }

  return <div className="advancing-template-grid">
    {props.allFormSections.map((section, idx) => (
      <TemplateEditSection
        key={idx}
        section={section}
        templateItems={props.templateItems}
        onChange={onTemplateItemChange}
        onSectionTypeChange={onSectionSettingChange}
      />
    ))}
  </div>
}
